import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import logo from '../../assets/logo.png';
import { useDocumentTitle } from '../../hooks/useDocumentTitle';

const SECTIONS = [
  {
    title: '1. Overview',
    body: `LocalContact ("we", "us", "our") is a credit-based live search platform operated by ADNAN MANNA DIGITAL (OPC) PRIVATE LIMITED. This Refund and Cancellation Policy explains when and how refunds are issued for purchases made on the platform. By purchasing credits or a plan, you agree to this policy.`,
  },
  {
    title: '2. Credit-Based Purchases',
    body: `LocalContact operates on a prepaid credit model. Credits are purchased upfront and consumed when searches are performed. Credits consumed during completed searches are non-refundable, as the service has already been delivered. If a search returns zero results, no credit is deducted.`,
  },
  {
    title: '3. Refund Eligibility for Unused Credits',
    body: `You may request a refund for unused credits within 7 days of purchase, provided no credits from that purchase have been consumed. After 7 days, or once any credit from the purchase has been used, the purchase is considered finalised and no refund will be issued. Refunds are processed to the original payment method within 5–10 business days.`,
  },
  {
    title: '4. Cancellation Policy',
    body: `LocalContact does not operate a recurring subscription model. All purchases are one-time credit pack transactions. There is no auto-renewal. You may stop using the service at any time. Your remaining credits remain valid until their stated expiry date (60 days from purchase).`,
  },
  {
    title: '5. Technical Failures',
    body: `If a search fails due to a technical error on our end (e.g., platform outage, system error), and credits were deducted, those credits will be restored to your account within 24 hours. If the issue is not automatically resolved, please contact us with the task ID and we will investigate and restore credits manually.`,
  },
  {
    title: '6. Duplicate Purchases',
    body: `If you accidentally make a duplicate purchase (same plan within 30 minutes), please contact us immediately at support@localcontact.ai with your payment confirmation. We will review the transaction and issue a full refund for the duplicate charge if confirmed.`,
  },
  {
    title: '7. Non-Refundable Situations',
    body: `Refunds will not be issued in the following cases: (a) credits that have been fully or partially consumed; (b) refund requests made more than 7 days after purchase; (c) accounts suspended or terminated for violation of our Terms of Service; (d) dissatisfaction with search results, as results reflect real-time data availability and accuracy cannot be guaranteed.`,
  },
  {
    title: '8. How to Request a Refund',
    body: `To request a refund, email us at support@localcontact.ai with: (a) your registered email address; (b) the payment reference or order ID; (c) the reason for the refund request. We will respond within 2 business days and process eligible refunds within 5–10 business days.`,
  },
  {
    title: '9. Governing Law',
    body: `This policy is governed by the laws of India. Any disputes relating to refunds shall be subject to the jurisdiction of the courts in Udupi, Karnataka, India.`,
  },
  {
    title: '10. Contact',
    body: `For any questions regarding this policy, please contact us at support@localcontact.ai or visit our Contact page.`,
  },
];

export default function RefundPolicy() {
  useDocumentTitle('Refund & Cancellation Policy');

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-white font-sans">
      <header className="border-b border-slate-100">
        <div className="mx-auto max-w-4xl px-6 py-5 flex items-center justify-between">
          <Link to="/">
            <img src={logo} alt="LocalContact" className="h-7 w-auto object-contain" />
          </Link>
          <Link to="/" className="text-[13px] font-bold text-slate-600 hover:text-slate-900 transition-colors">
            Back to home
          </Link>
        </div>
      </header>

      <div className="mx-auto max-w-3xl px-6 py-16 pb-24">
        <div className="mb-12">
          <p className="text-[11px] font-black uppercase tracking-[0.18em] text-brand-700 mb-3">Legal</p>
          <h1 className="text-4xl sm:text-5xl font-black tracking-[-0.035em] text-slate-900 mb-4">Refund &amp; Cancellation Policy</h1>
          <p className="text-[14px] text-slate-400 font-medium">Last updated: June 2025</p>
        </div>

        <div className="prose prose-slate max-w-none space-y-10">
          {SECTIONS.map((section) => (
            <div key={section.title}>
              <h2 className="text-[17px] font-bold text-slate-900 mb-3">{section.title}</h2>
              <p className="text-[15px] text-slate-600 leading-relaxed">{section.body}</p>
            </div>
          ))}
        </div>

        <div className="mt-16 pt-8 border-t border-slate-100 flex flex-wrap gap-4 text-[13px] text-slate-400">
          <Link to="/terms" className="hover:text-slate-700 transition-colors font-medium">Terms of Service</Link>
          <span>·</span>
          <Link to="/privacy" className="hover:text-slate-700 transition-colors font-medium">Privacy Policy</Link>
          <span>·</span>
          <Link to="/contact" className="hover:text-slate-700 transition-colors font-medium">Contact Us</Link>
          <span>·</span>
          <Link to="/" className="hover:text-slate-700 transition-colors font-medium">Back to home</Link>
        </div>
      </div>
    </div>
  );
}
